import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as BABYLON from 'babylonjs';
import { UserDocument } from '../../interfaces/user-document.interface';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { SceneSetupService } from '../../services/babylon.service';
import { GifterDetailComponent } from '../gifter-detail/gifter-detail.component';

@Component({
  selector: 'app-gifter-scene',
  standalone: true,
  imports: [CommonModule, GifterDetailComponent],
  templateUrl: './gifter-scene.component.html',
  styleUrl: './gifter-scene.component.css'
})
export class GifterSceneComponent implements OnChanges, OnDestroy {
  @Input() engine!: BABYLON.Engine;
  @Input() scene!: BABYLON.Scene;
  @Input() userData!: UserDocument;
  @Output() backButtonClicked: EventEmitter<void> = new EventEmitter<void>();

  private player: BABYLON.Mesh | null = null;
  private camera: BABYLON.Camera | null = null;

  selectedGifter: any = null;
  selectedGifterInfo: any = null;

  private gifterMeshes: BABYLON.Mesh[] = [];
  private floorMesh: BABYLON.Mesh | null = null;

  constructor(
    private firestore: Firestore,
    private sceneSetupService: SceneSetupService,
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['scene'] || changes['userData']) && this.scene && this.userData) {
      this.initializeGifterScene();
    }
  }

  initializeGifterScene() {
    if (!this.scene || !this.userData) return;

    this.destroyAll();

    // Thêm skybox gradient
    this.sceneSetupService.createGradientSkybox(this.scene);

    // Tạo room và lấy gifter meshes + floor
    const roomData = this.sceneSetupService.createFamilyHealthRoom(this.scene, this.userData);
    this.gifterMeshes = roomData.gifterMeshes || [];
    this.floorMesh = roomData.floor || null;
    
    // Tạo gifter player với click handler để về home
    this.player = this.sceneSetupService.createGifterPlayer(this.scene);
    
    // Thêm click handler cho player để về home
    this.setupPlayerBackNavigation();

    this.camera = this.sceneSetupService.createFirstPersonCamera(this.scene, this.engine.getRenderingCanvas()!, this.player);
    this.scene.activeCamera = this.camera;

    // Sau khi đã tạo player và camera
    if (this.camera && this.player && this.camera instanceof BABYLON.ArcRotateCamera) {
      this.scene.onBeforeRenderObservable.add(() => {
        (this.camera as BABYLON.ArcRotateCamera).setTarget(this.player!.position);
      });
    }

    // Xử lý click để di chuyển player
    this.setupPlayerMovement();

    // Xử lý click vào gifter
    this.setupGifterInteraction();
  }

  // Method mới để setup click handler cho player
  private setupPlayerBackNavigation(): void {
    if (!this.player || !this.scene) return;

    // Thêm action manager cho player
    this.player.actionManager = new BABYLON.ActionManager(this.scene);
    
    // Thêm click handler
    this.player.actionManager.registerAction(
      new BABYLON.ExecuteCodeAction(BABYLON.ActionManager.OnPickTrigger, async () => {
        console.log('Gifter player clicked - going back to home');
        await this.goBackToHome();
      })
    );

    // Thêm hover effect để user biết có thể click
    this.player.actionManager.registerAction(
      new BABYLON.ExecuteCodeAction(BABYLON.ActionManager.OnPointerOverTrigger, () => {
        if (this.player && this.player.material) {
          (this.player.material as BABYLON.StandardMaterial).emissiveColor = new BABYLON.Color3(0.2, 0.2, 0.2);
        }
      })
    );

    this.player.actionManager.registerAction(
      new BABYLON.ExecuteCodeAction(BABYLON.ActionManager.OnPointerOutTrigger, () => {
        if (this.player && this.player.material) {
          (this.player.material as BABYLON.StandardMaterial).emissiveColor = new BABYLON.Color3(0, 0, 0);
        }
      })
    );
  }

  // Method để xử lý việc quay về home
  private async goBackToHome(): Promise<void> {
    try {
      console.log('Starting cleanup before going back to home');
      
      // Xóa tất cả components trong scene
      await this.destroyAll();
      
      console.log('Cleanup completed - emitting backButtonClicked event');
      
      // Emit event để parent component (home) xử lý
      this.backButtonClicked.emit();
      
    } catch (error) {
      console.error('Error during goBackToHome:', error);
      // Vẫn emit event ngay cả khi có lỗi
      this.backButtonClicked.emit();
    }
  }

  private setupPlayerMovement(): void {
    if (!this.scene) return;

    this.scene.onPointerObservable.add((pointerInfo) => {
      if (pointerInfo.type === BABYLON.PointerEventTypes.POINTERDOWN) {
        const pickResult = pointerInfo.pickInfo;
        
        // Kiểm tra nếu click vào floor (để di chuyển player)
        if (pickResult?.hit && pickResult.pickedMesh?.name === 'floor' && this.player) {
          const target = pickResult.pickedPoint;
          if (target) {
            this.movePlayerTo(target);
          }
        }
        // Không cần xử lý click vào player ở đây vì đã có ActionManager
      }
    });
  }

  private setupGifterInteraction(): void {
    if (!this.scene) return;

    this.scene.onPointerObservable.add((pointerInfo) => {
      if (pointerInfo.type === BABYLON.PointerEventTypes.POINTERDOWN) {
        const pickResult = pointerInfo.pickInfo;
        if (pickResult?.hit && pickResult.pickedMesh?.name?.startsWith('gifter_')) {
          const gifterMesh = pickResult.pickedMesh;
          const gifterData = gifterMesh.metadata?.gifter;
          if (gifterData) {
            this.onGifterClick(gifterData);
          }
        }
      }
    });
  }

  private movePlayerTo(target: BABYLON.Vector3): void {
    if (!this.player || !this.scene) return;

    // Đảm bảo player không đi xuống dưới sàn
    const adjustedTarget = new BABYLON.Vector3(target.x, 0.35, target.z);

    // Tính toán quãng đường di chuyển
    const distance = BABYLON.Vector3.Distance(this.player.position, adjustedTarget);

    // Tính số vòng quay dựa trên bán kính (bán kính = diameter / 2)
    const radius = this.player.getBoundingInfo().boundingSphere.radiusWorld;
    const rotationAmount = distance / radius; // Số radian quay quanh trục

    // Animation di chuyển
    const moveAnimation = new BABYLON.Animation(
      'playerMove',
      'position',
      60, // fps
      BABYLON.Animation.ANIMATIONTYPE_VECTOR3,
      BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT
    );
    const keys = [
      { frame: 0, value: this.player.position.clone() },
      { frame: 60, value: adjustedTarget }
    ];
    moveAnimation.setKeys(keys);

    // Animation xoay quanh trục vuông góc với hướng di chuyển (trục "bóng lăn")
    const direction = adjustedTarget.subtract(this.player.position).normalize();
    const up = new BABYLON.Vector3(0, 1, 0);
    const rollAxis = BABYLON.Vector3.Cross(up, direction).normalize();

    // Tạo quaternion ban đầu và cuối
    const startRotation = this.player.rotationQuaternion || BABYLON.Quaternion.Identity();
    const endRotation = BABYLON.Quaternion.RotationAxis(rollAxis, rotationAmount).multiply(startRotation);

    const rotateAnimation = new BABYLON.Animation(
      'playerRoll',
      'rotationQuaternion',
      60,
      BABYLON.Animation.ANIMATIONTYPE_QUATERNION,
      BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT
    );
    rotateAnimation.setKeys([
      { frame: 0, value: startRotation.clone() },
      { frame: 60, value: endRotation }
    ]);

    // Đảm bảo player dùng quaternion để xoay mượt
    this.player.rotationQuaternion = startRotation.clone();

    this.player.animations = [moveAnimation, rotateAnimation];

    // Bắt đầu animation
    this.scene.beginAnimation(this.player, 0, 60, false, 1.5); // tốc độ 1.5x, có thể chỉnh
  }

  private onGifterClick(gifterData: any): void {
    this.selectedGifter = gifterData;
    
    // Load thông tin chi tiết của gifter
    if (gifterData.userDocId) {
      this.loadGifterInfo(gifterData.userDocId);
    }
  }

  async loadGifterInfo(userDocId: string): Promise<void> {
    try {
      const userDocRef = doc(this.firestore, `User/${userDocId}`);
      const userSnapshot = await getDoc(userDocRef);
      
      if (userSnapshot.exists()) {
        this.selectedGifterInfo = userSnapshot.data();
      } else {
        this.selectedGifterInfo = { displayName: 'Không tìm thấy', organize: 'Không có' };
      }
    } catch (error) {
      console.error('Error loading gifter info:', error);
      this.selectedGifterInfo = { displayName: 'Lỗi tải dữ liệu', organize: 'Không có' };
    }
  }

  closeGifterDetail(): void {
    this.selectedGifter = null;
    this.selectedGifterInfo = null;
  }


  ngOnDestroy(): void {
    this.destroyAll();
  }

  private async destroyAll(): Promise<void> {
    try {
      console.log('Starting destroyAll cleanup');

      // Xóa action manager của player trước
      if (this.player && this.player.actionManager) {
        this.player.actionManager.dispose();
        this.player.actionManager = null;
      }

      // Xóa camera
      if (this.camera) {
        this.camera.dispose();
        this.camera = null;
      }

      // Xóa player
      if (this.player) {
        this.player.dispose();
        this.player = null;
      }

      // Xóa gifterMeshes
      this.gifterMeshes.forEach(mesh => {
        if (mesh && mesh.actionManager) {
          mesh.actionManager.dispose();
        }
        if (mesh) mesh.dispose();
      });
      this.gifterMeshes = [];

      // Xóa floor
      if (this.floorMesh) {
        this.floorMesh.dispose();
        this.floorMesh = null;
      }

      // Xóa skybox
      const skybox = this.scene?.getMeshByName('skyBox');
      if (skybox) {
        skybox.dispose();
      }

      // Clear observables
      this.scene?.onPointerObservable.clear();
      this.scene?.onBeforeRenderObservable.clear();

      // Xóa tất cả meshes còn lại
      const meshesToDispose = this.scene?.meshes.slice() || [];
      meshesToDispose.forEach(mesh => {
        if (mesh.actionManager) {
          mesh.actionManager.dispose();
        }
        mesh.dispose();
      });

      // Xóa materials và textures
      const materialsToDispose = this.scene?.materials.slice() || [];
      materialsToDispose.forEach(material => material.dispose());

      const texturesToDispose = this.scene?.textures.slice() || [];
      texturesToDispose.forEach(texture => texture.dispose());

      console.log('destroyAll cleanup completed');
      await new Promise(resolve => setTimeout(resolve, 100));

    } catch (error) {
      console.error('Error during destroyAll:', error);
    }
  }
  
}
